import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  NgZone, OnInit, OnDestroy,
  ViewChild
} from '@angular/core';
import {MatCardModule} from "@angular/material/card";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatButtonModule} from "@angular/material/button";
import {MatMenuModule} from "@angular/material/menu";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {DOCUMENT, NgForOf, NgIf, NgStyle} from "@angular/common";
import {HttpClient} from "@angular/common/http";
import {MatBottomSheet} from "@angular/material/bottom-sheet";
import {Title} from "@angular/platform-browser";
import {MidiPlayerService} from "../service/midi-player.service";
import {HymnSelectionService} from "../service/hymn-selection.service";
import {LayoutService} from "../service/layout.service";
import {ShareService} from "../service/share.service";
import {SidenavService} from "../service/sidenav.service";
import {SettingsService} from "../service/settings.service";
import {HymnSection} from "../hymn/HymnSection";
import {hymnsDetailed} from "../hymn/hymnsDetailed";
import {debounce, debounceTime, fromEvent} from "rxjs";
import {FontSizeComponent} from "../controls/font-size/font-size.component";
import {MusicControlComponent} from "../controls/music-control/music-control.component";
import {ModalService} from "../service/modal.service";
import {Hymn} from "../hymn/hymn";
import {translation} from "../translation";
import {DoubleTapDirective} from "../directive/double-tap.directive";
import {SwipeGestureDirective} from "../directive/swipe-gesture.directive";

@Component({
  selector: 'app-hymn-presenter',
  standalone: true,
  imports: [
    MatIconModule,
    MatCardModule,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    RouterLink,
    NgIf,
    NgForOf,
    NgStyle,
    MusicControlComponent,
    DoubleTapDirective,
    SwipeGestureDirective
  ],
  templateUrl: './hymn-presenter.component.html',
  styleUrl: './hymn-presenter.component.scss'
})
export class HymnPresenterComponent implements OnInit, AfterViewInit, OnDestroy {

  lastHymnNumber = hymnsDetailed[hymnsDetailed.length - 1].hymnNumber;

  Number = Number;

  @ViewChild('content') public content!: ElementRef;
  @ViewChild('hymnTitle') public hymnTitle!: ElementRef;
  @ViewChild('verseNumber') public hymnNumber!: ElementRef;
  @ViewChild('hymnText') public hymnText!: ElementRef;
  contentHeight!: number;
  containerHeight!: number;
  width!: number;

  hymn: any = null;
  elem: any;

  verseList: any[] = [];
  activeVerseIndex = 0;
  activeVerse = '1';

  fontsize = 12;

  swipeThreshold = 70;

  // Pointer Events state for swipe gestures
  private swipeStartX = 0;
  private swipeStartY = 0;
  private swipeStartTime = 0;
  private activePointers = new Map<number, PointerEvent>();

  // Bound handlers for add/remove
  private peDown?: (ev: PointerEvent) => void;
  private peMove?: (ev: PointerEvent) => void;
  private peUpCancel?: (ev: PointerEvent) => void;

  maxFontSize: number | null;
  crossVerseConsistentFontSize: number | null;

  activeVerseLines: string[] = [];
  visibleVerseSections = new Map<number, HymnSection>();
  linesPerVerse = this.settingService.getPresentationModeVisibleLines();
  linesPerVerseBase = this.linesPerVerse;
  visibleSectionIndex = 0;

  protected textSizeStyle = this.fontsize + 'px';

  // Font family and weight for presentation mode
  presentFontFamily: string | undefined = this.settingService.getFontFamily();
  presentFontWeight: number | undefined = this.settingService.getFontWeight();

  constructor(
    @Inject(DOCUMENT) private document: any,
    private http: HttpClient,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private bottomSheet: MatBottomSheet,
    private titleService: Title,
    public router: Router,
    public midiService: MidiPlayerService,
    public hymnSelectionService: HymnSelectionService,
    public layoutService: LayoutService,
    public shareService: ShareService,
    public sidenavService: SidenavService,
    protected settingService: SettingsService,
    protected modalService: ModalService,
    private ref: ChangeDetectorRef) {

    this.maxFontSize = null;
    this.crossVerseConsistentFontSize = null;

    fromEvent(window, 'resize').pipe(debounceTime(100)).subscribe(() => {
      this.onResize();
    });

    // Subscribe to global font family changes
    this.settingService.fontFamilySubject.subscribe((f) => {
      this.ngZone.run(() => {
        this.presentFontFamily = f;
      });
    });

    // Subscribe to global font weight changes
    this.settingService.fontWeightSubject.subscribe((w) => {
      this.ngZone.run(() => {
        this.presentFontWeight = (w ?? 400);
      });
    });
  }

  toggleListSidenav() {
    this.sidenavService.toggleList();
  }

  onSwipePrevious() {
    this.navigateLeft();
    this.setActiveVerse();
    console.debug('New active verse = ' + this.activeVerse);
    setTimeout(() => { this.ref.detectChanges(); }, 1);
    this.resetAndCheckFontSize();
  }

  onSwipeNext() {
    this.navigateRight();
    this.setActiveVerse();
    console.debug('New active verse = ' + this.activeVerse);
    setTimeout(() => { this.ref.detectChanges(); }, 1);
    this.resetAndCheckFontSize();
  }


  toggleSettingSidenav() {
    this.settingService.toggleSettings();
  }

  ngOnInit() {
    this.settingService.setPresentationViewMode();
    console.debug('Lines per verse = ' + this.settingService.getPresentationModeVisibleLines());
    this.linesPerVerseBase = this.settingService.getPresentationModeVisibleLines();
    this.linesPerVerse = this.linesPerVerseBase;
    this.elem = document.documentElement;
    this.initialize();
    this.maxFontSize = null;
    this.crossVerseConsistentFontSize = null;
  }

  ngAfterViewInit(): void {
    const el = this.content?.nativeElement as HTMLElement | undefined;
    if (el && (!el.style.touchAction || el.style.touchAction === 'auto')) {
      el.style.touchAction = 'pan-y';
    }
  }

  ngOnDestroy(): void {
    // No manual listeners to clean up after using directives
  }

  getLastHymn() {
    if(this.settingService.getHistory() && this.settingService.getHistory().length > 0){
      return this.settingService.getHistory()[0];
    } else {
      return hymnsDetailed[Math.floor(Math.random() * hymnsDetailed.length)];
    }
  }

  initialize() {
    this.route.paramMap.subscribe(params => {
      if (params.get('reference')) {
        this.hymnSelectionService.selectedHymn = hymnsDetailed[+params.get('reference')! - 1];
        this.getJSON(params.get('reference'));
      } else {
        this.hymn = this.getLastHymn();
        this.hymnSelectionService.selectedHymn = this.hymn;
      }
      this.setUpVerseOrder();
      this.loadActiveVerseLines();
      this.titleService.setTitle(`${translation.hymn} ${this.hymn.hymnNumber} - ${this.hymn.title} - ${translation.title_full}`);
      this.crossVerseConsistentFontSize = null;
      this.resetAndCheckFontSize();
      this.midiService.stop();
    });
  }

  setUpVerseOrder() {
    this.verseList = [];

    this.activeVerseIndex = 0;
    this.visibleSectionIndex = 0;

    if(this.hymn.startsWithChorus) {
      this.verseList.push('R');
      console.debug('R');
      this.activeVerse = 'R';
    } else {
      this.activeVerse = '1';
    }

    let count: number;
    for (count = 1; count <= this.hymn.numberOfVerses; count++) {
      this.verseList.push(String(count));
      console.debug(count);
      if (this.hymn.hasChorus) {
        console.debug('Hymn has chorus');
        this.verseList.push('R');
        console.debug('R');
      }
      else {
        console.debug('Hymn does not have chorus');
      }
    }
  }


  setHeights() {
    this.textSizeStyle = this.fontsize + 'px';
  }

  clickVerse(verseNumber: string) {
    if (!this.hymn.hasChorus) {
      this.activeVerseIndex = Number(verseNumber) - 1;
      this.visibleSectionIndex = 0;
    }
    else {
      if (verseNumber === 'R') {
        if (this.activeVerseIndex < this.verseList.length - 1) {
          this.activeVerseIndex++;
          this.visibleSectionIndex = 0;
        }
      } else {
        this.activeVerseIndex = (Number(verseNumber) * 2) - 2;
        this.visibleSectionIndex = 0;
      }
    }
    this.setActiveVerse();
    console.debug('New active verse = ' + this.activeVerse);
    setTimeout(() => { this.ref.detectChanges(); }, 1);
    this.resetAndCheckFontSize();
  }

  public setActiveVerse() {
    this.activeVerse = this.verseList[this.activeVerseIndex];
    this.loadActiveVerseLines();
  }

  public getJSON(reference: string | null) {
    if(reference) {
      this.hymn = hymnsDetailed[+reference - 1];
    }
  }

  toggleFullscreen() {
    if (this.layoutService.fullscreen) {
      this.layoutService.exitFullScreen();
    }
    else {
      this.layoutService.enterFullScreen();
    }
  }

  navigateLeft() {
    if (this.visibleSectionIndex > 0) {
      this.visibleSectionIndex--;
      setTimeout(() => { this.ref.detectChanges(); }, 1);
      this.resetAndCheckFontSize();
    } else if (this.activeVerseIndex > 0) {
      this.activeVerseIndex--;
      this.setActiveVerse();
      this.visibleSectionIndex = this.visibleVerseSections.size-1;
      console.debug('New active verse = ' + this.activeVerse);
      setTimeout(() => { this.ref.detectChanges(); }, 1);
      this.resetAndCheckFontSize();
    }
  }

  navigateRight() {
    if (this.visibleSectionIndex < this.visibleVerseSections.size - 1) {
      this.visibleSectionIndex++;
      setTimeout(() => { this.ref.detectChanges(); }, 1);
      this.resetAndCheckFontSize();
    } else if (this.activeVerseIndex < (this.verseList.length - 1)) {
      this.activeVerseIndex++;
      this.visibleSectionIndex = 0;
      this.setActiveVerse();
      console.debug('New active verse = ' + this.activeVerse);
      setTimeout(() => { this.ref.detectChanges(); }, 1);
      this.resetAndCheckFontSize();
    }
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    this.ngZone.run(() => {
      if (!this.hymnSelectionService.searchFocus && !this.hymnSelectionService.dialpadActive) {
        switch (event.key) {
          case 'ArrowLeft':
          case 'b':
            this.navigateLeft();
            break;
          case 'ArrowRight':
          case 'n':
            this.navigateRight();
            break;
          case 'r':
          case 'R':
            if (this.hymn.hasChorus && this.activeVerseIndex % 2 === 0) {
              if (this.activeVerseIndex < (this.verseList.length - 1)) {
                this.activeVerseIndex++;
              } else {
                this.activeVerseIndex--;
              }
              this.visibleSectionIndex = 0;
              this.setActiveVerse();
              console.debug('New active verse = ' + this.activeVerse);
              setTimeout(() => { this.ref.detectChanges(); }, 1);
              this.resetAndCheckFontSize();
            }
            break;
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpad();
            break;
          case 'l':
          case 'L':
            this.sidenavService.toggleList();
            break;
          case 'w':
          case 'W':
            this.hymnSelectionService.goToRandomHymn(false);
            break;
          case 'f':
          case 'F':
            this.layoutService.fullscreen ?
              this.layoutService.exitFullScreen() : this.layoutService.enterFullScreen();
            break;
          case 'g':
          case 'G':
            this.hymnSelectionService.goToRandomHymn(true);
            break;
          case 's':
          case 'S':
            this.midiService.playing ?
              this.midiService.stop() : this.midiService.playMidi(this.hymn, this.settingService.getInstruments(), this.hymn.verses.length);
            break;
          case '+':
            if(this.linesPerVerse < 7) {
              this.applyLinesPerVerseChange(this.linesPerVerse + 1);
            }
            break;
          case '-':
            if(this.linesPerVerse > 1) {
              this.applyLinesPerVerseChange(this.linesPerVerse - 1);
            }
            break;
          case '<':
          case ',':
            this.midiService.decreasePlaybackSpeed();
            break;
          case '>':
          case '.':
            this.midiService.increasePlaybackSpeed();
            break;
          case 'm':
            this.midiService.setPlaybackSpeed(1.0);
            break;
          case 'v':
          case 'V':
            this.router.navigate([`/${translation.path_hymn}/`+this.hymn.hymnNumber]);
            break;
          case 'i':
            if(!this.modalService.active) {
              this.modalService.openDialog('shortcuts')
            } else {
              this.modalService.closeDialog();
            }
            break;
          case 'Escape':
            if(this.modalService.active) {
              this.modalService.closeDialog();
            }
            break;
          default:
            if (!isNaN(+event.key)) {
              const verseNumber = Number(event.key);
              if (verseNumber > 0 && verseNumber <= this.hymn.numberOfVerses) {
                if (this.hymn.hasChorus) {
                  this.activeVerseIndex = (verseNumber - 1) * 2;
                } else {
                  this.activeVerseIndex = verseNumber - 1;
                }
                this.visibleSectionIndex = 0;
                this.setActiveVerse();
                console.debug('New active verse = ' + this.activeVerse);
                setTimeout(() => { this.ref.detectChanges(); }, 1);
                this.resetAndCheckFontSize();
              }
            }
        }
      } else {
        switch (event.key) {
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpadCancel();
            break;
          case 'Escape':
            this.hymnSelectionService.dialpadCancel();
            this.hymnSelectionService.searchFocus = false;
            break;
        }
      }
    });
  }

  checkFontSize() {
    if (this.maxFontSize == null) {
      this.contentHeight = this.content.nativeElement.scrollHeight;
      this.containerHeight = this.content.nativeElement.clientHeight;
      console.debug('Content height = ' + this.contentHeight);
      console.debug('Window height = ' + this.containerHeight);

      if (this.contentHeight <= this.containerHeight && this.fontsize <= 1000) {
        console.debug('Content fits in window');
        while (this.contentHeight <= this.containerHeight && this.fontsize <= 200 && (this.crossVerseConsistentFontSize == null || this.fontsize < this.crossVerseConsistentFontSize)) {
          this.fontsize++;
          //console.debug('Fontsize = ' + this.fontsize);
          this.setHeights();
          this.contentHeight = this.content.nativeElement.scrollHeight;
          this.containerHeight = this.content.nativeElement.clientHeight;
          this.ref.detectChanges();
          if (this.contentHeight === 0) {
            console.debug('Content Height is 0 for some reason...');
            break;
          }
        }

        while (this.contentHeight > this.containerHeight) {
          this.fontsize--;
          //console.debug('Fontsize = ' + this.fontsize);
          this.setHeights();
          this.contentHeight = this.content.nativeElement.scrollHeight;
          this.containerHeight = this.content.nativeElement.clientHeight;
          this.ref.detectChanges();
          if (this.contentHeight === 0) {
            console.debug('Content Height is 0 for some reason...');
            break;
          }
        }

        this.maxFontSize = this.fontsize;
        if(this.crossVerseConsistentFontSize == null || this.maxFontSize < this.crossVerseConsistentFontSize) {
          this.crossVerseConsistentFontSize = this.fontsize;
        }
      }
      else {
        console.debug('Content does not fit in window');
        while (this.fontsize > 7 && this.contentHeight > this.containerHeight && this.fontsize) {
          this.fontsize--;
          //console.debug('Fontsize = ' + this.fontsize);
          this.setHeights();
          this.contentHeight = this.content.nativeElement.scrollHeight;
          this.containerHeight = this.content.nativeElement.clientHeight;
          this.ref.detectChanges();
          if (this.contentHeight === 0) {
            console.debug('Content Height is 0 for some reason...');
            break;
          }
        }

        this.maxFontSize = this.fontsize;
        if(this.crossVerseConsistentFontSize == null || this.maxFontSize < this.crossVerseConsistentFontSize) {
          this.crossVerseConsistentFontSize = this.fontsize;
        }
      }

      console.debug('Content height = ' + this.contentHeight);
      console.debug('Window height = ' + this.containerHeight);
    }
  }

  loadActiveVerseLines() {
    this.visibleVerseSections.clear();
    const verseContents = (this.activeVerse != 'R' ? this.hymn.verses[Number(this.activeVerse) - 1].content : Hymn.getChorus(this.hymn, Number(this.activeVerse+1)));
    if(verseContents) {
      this.activeVerseLines = verseContents;

      for (let i = 0; i < this.activeVerseLines.length; i++) {
        const sectionIndex = Math.floor(i / this.linesPerVerse);
        let section: HymnSection | undefined;
        if (this.visibleVerseSections.has(sectionIndex)) {
          section = this.visibleVerseSections.get(sectionIndex);
        } else {
          section = {lines: []};
          this.visibleVerseSections.set(sectionIndex, section);
        }
        if(section) {
          section.lines[i % this.linesPerVerse] = this.activeVerseLines[i];
        }
      }

      const size = this.visibleVerseSections.size;
      if (size > 1) {
        for (let i = 0; i < size; i++) {
          let section = this.visibleVerseSections.get(i);
          if (section && i < size - 1) {
            section.lines[section.lines.length - 1] += ' ...';
          }
          if (section && i > 0) {
            section.lines[0] = '... ' + section.lines[0];
          }
        }
      }

      console.debug(this.visibleVerseSections);
    }
  }

  changeFontSize() {
    this.layoutService.fontSizeSheetActive = true;

    const bottomSheetRef = this.bottomSheet.open(FontSizeComponent, {
      data: {
        title: translation.lines_per_verse,
        // visibility flags
        fontSizeEnabled: false,
        linesPerVerseEnabled: true,
        // lines per verse slider params
        linesPerVerseMin: 1,
        linesPerVerseMax: 7,
        currentLinesPerVerse: this.settingService.getPresentationModeVisibleLines(),
        linesPerVerseChange: new EventEmitter<number>(),
        // font family controls
        fonts: this.settingService.getAvailableFonts(),
        currentFontFamily: this.settingService.getFontFamily(),
        fontFamilyChange: new EventEmitter<string | undefined>(),
        // font weight controls (global)
        fontWeightEnabled: true,
        currentFontWeight: this.settingService.getFontWeight(),
        fontWeightChange: new EventEmitter<number | undefined>(),
        // text outline toggle (global)
        textOutlineToggleEnabled: true,
        textOutlineEnabled: this.settingService.getTextOutlineEnabled(),
        textOutlineChange: new EventEmitter<boolean>(),

        textAlignmentEnabled: true,
        currentTextAlignment: this.settingService.getTextAlignmentPresentationMode(),
        textAlignmentChange: new EventEmitter<string>()
      },
    });

    bottomSheetRef.instance.data.linesPerVerseChange?.subscribe((linesPerVerse: number) => {
      this.applyLinesPerVerseChange(linesPerVerse);
    });

    // font family change
    bottomSheetRef.instance.data.fontFamilyChange?.subscribe((fam: string | undefined) => {
      this.settingService.setPresentationModeFontFamily(fam);
    });

    // font weight change
    bottomSheetRef.instance.data.fontWeightChange?.subscribe((w: number | undefined) => {
      this.settingService.setFontWeight(w);
    });

    // text outline toggle
    bottomSheetRef.instance.data.textOutlineChange?.subscribe((enabled: boolean) => {
      this.settingService.setTextOutlineEnabled(enabled);
    });

    // text alignment change
    bottomSheetRef.instance.data.textAlignmentChange?.subscribe((al: string) => {
      this.settingService.setTextAlignmentPresentationMode(al);
    });

    bottomSheetRef.afterDismissed().subscribe(() => {
      this.layoutService.fontSizeSheetActive = false;
      this.settingService.setPresentationModeVisibleLines(this.linesPerVerse);
    });
  }

  applyLinesPerVerseChange(linesPerVerse: number) {
    this.settingService.setPresentationModeVisibleLines(linesPerVerse);

    if (this.settingService.getPresentationModeVisibleLines() !== this.linesPerVerse) {
      this.linesPerVerse = this.settingService.getPresentationModeVisibleLines();
      this.visibleSectionIndex = 0;
      this.loadActiveVerseLines();
      this.crossVerseConsistentFontSize = null;
      this.resetAndCheckFontSize();
    }
  }

  resetAndCheckFontSize() {
    setTimeout(() => {
      this.maxFontSize = null;
      this.checkFontSize();
    }, 10);
  }

  onResize() {
    this.crossVerseConsistentFontSize = null;
    this.resetAndCheckFontSize();
  }

  protected readonly translation = translation;

  async onDoubleTap() {
    if(this.layoutService.fullscreenSupported()) {
      await this.layoutService.toggleFullScreen();
    }
  }
}
